import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { PosScreen, Header, Content, Footer, PhoneOnly, BackActionButton, Filler, AcceptActionButton, LargeDeviceOnly } from 'pos/ui';
import { Res } from 'core/api';
import { Modal } from 'core/ui-next/modal';
import { VStack, Text, Box } from '@chakra-ui/react';
import { BasicButton, SvgIcon, useEffectOnce } from 'core/ui-next';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { zInt } from 'core/infrastructure-next';

type Props = {
	initial: Nullable<number>;
	ticketLocation: string;
	maxTableCompanions: number;
} & Modal.Actions<Nullable<number>>;

function GetTableCompanions(props: Props) {
	const { initial, ticketLocation, maxTableCompanions, cancel, close } = props;

	const stateSchema = z.object({
		companions: zInt({ nonNegative: true }).nullable()
			.refine(val => val === null || val <= maxTableCompanions, {
				message: Res.Msg.MSG_TABLE_COMPANION_CANNOT_BE_GREATER_THAN(maxTableCompanions)
			}),
	});

	type State = z.infer<typeof stateSchema>;

	const formRef = React.useRef<FormRef<State>>(null);

	useEffectOnce(() => {
		formRef.current?.setFocus?.('companions');
	});

	const { Form, IntInput } = useControlsFor<State>();

	const onAccept = async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		if (result.value.companions === 0) {
			result.value.companions = null;
		}
		close(result.value.companions);
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return <PosScreen screenId='GET_TABLE_COMPANIONS' size={'small'} useKeyPad={true}>
		<Header title={Res.Web.GET_TABLE_COMPANIONS()} onClose={cancel} />
		<Content>
			<VStack paddingX={'6'} paddingY={'9'} h='full' w='full' gap={'6'}>
				<VStack gap={'4'} justifyItems={'center'} w='full'>
					<SvgIcon src={Images.TableCompanions} w={'12'} h={'12'} />
					<VStack gap={'2'} justifyItems={'center'} w='full' textAlign={'center'}>
						<Text fontSize={'md'} fontWeight={'bold'}>
							{ticketLocation}
						</Text>
						<Text fontSize={'sm'} fontWeight={'normal'}>
							{Res.Web.GET_TABLE_COMPANIONS_TABLE_COMPANIONS_LBL()}
						</Text>
					</VStack>
				</VStack>
				<VStack w={'full'} justifyContent={'center'}>
					<Box w={'full'}>
						<Form ref={formRef} initialValue={{ companions: initial === null ? 0 : initial }} schema={stateSchema}>
							<IntInput linkTo='companions' placeholder={Res.Web.GET_TABLE_COMPANIONS()} onKeyDown={UI.whenEnterKey(onAccept)} />
						</Form>
					</Box>
				</VStack>
				<LargeDeviceOnly>
					<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} />
				</LargeDeviceOnly>
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
}

export default async function getTableCompanions(ticket: { TableCompanions: Nullable<number>, FriendlyName: string, Location: { SaleCenterName: string, SaleLocationName: string } }, maxTableCompanions = 99): Promise<Result<Nullable<number>>> {
	const { TableCompanions: currentTableCompanions } = ticket;
	const formattedTicketLocation = UI.Format.asLocationDescription(ticket);

	return Modal.show(GetTableCompanions, {
		initial: currentTableCompanions,
		ticketLocation: formattedTicketLocation,
		maxTableCompanions,
	});
}
